import * as React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import * as DateHelpers from './DateHelpers';
import NotesPopup from './NotesPopup';
import JobSearchControlData from './JobSearchControl';
import SchedulePickupConfirmData from './SchedulePickupConfirm';
import { JobSearchResults } from './models/JobInterfaces';
import { useState } from 'react';

const win: any = window;
const Stimulsoft = win.Stimulsoft;

interface AddNewResults {
    itemIndex: number;
    selected: boolean;
    isEws: boolean;
    jobQuoteItemId: number;
    jobId: number;
    siteAddress: string;
    customerId: number;
    customer: string;
    customerCreditCard: boolean;    //true if the customer is prepaid and paying by credit card
    customerDirectDebit: boolean;   //true if the customer is prepaid and paying by credit card
    customerSendConfirmationEmail: boolean;    //false if customer isn't setup for emails
    purchaseOrderNumber: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    itemShortcut: string;
    type: string;
    quantityTotal: number;
    quantityScheduledAvailable: number;     //cater for part pickups
    quantityScheduled: number;
    quantityType: string;
    hirePeriod: string;
    hirePeriodActual: number;
    offhireDate: string;
    endhireDate: string;
    pickupDate: string;
    bringForwardDate: string;
    truck: number;
    canChangeTruck: boolean;
    notes: string;
}

interface EditNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface ConfirmJobIdList {
    jobId: number;
    hasInstall: boolean;    //at least one install for this job - need to show payment method for installs only (prepaid customer)
}

const SchedulePickupData = () => {
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [jobTableKey, setJobTableKey] = useState(0);  //used to clear job table
    const [selectedJobs, setSelectedJobs] = useState<JobSearchResults[]>([]);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState<AddNewResults[]>([]);
    const [showEditNotesModal, setShowEditNotesModal] = useState(false);
    const initEditNotes: EditNotes = {
        heading: "",
        parentIndex: 0,
        rowIndex: 0,
        id: 0,
        itemId: 0,
        item: "",
        notes: ""
    };
    const [editNotes, setEditNotes] = useState<EditNotes>(initEditNotes);
    const [sendEmailToCustomer, setSendEmailToCustomer] = useState(true);
    const [truckOptions, setTruckOptions] = useState<GenericList[]>([]);
    const [yesNoOptions, setYesNoOptions] = useState<GenericList[]>([]);
    const [tiltSlideClearance, setTiltSlideClearance] = useState(-1);
    const [plumbingElectricityDisconnected, setPlumbingElectricityDisconnected] = useState(-1);
    const [disconnectionDate, setDisconnectionDate] = useState("");
    const [pickupTravelPriceNote, setPickupTravelPriceNote] = useState("");
    const [pickupTravelExtraHoursPriceNote, setPickupTravelExtraHoursPriceNote] = useState("");
    const [weekdayAccess, setWeekdayAccess] = useState(-1);
    const [phoneCallRequired, setPhoneCallRequired] = useState(-1);
    const [jobPickedUp, setJobPickedUp] = useState(-1);
    const [fencePartPickup, setFencePartPickup] = useState(-1);
    const [fencePartPickupPanels, setFencePartPickupPanels] = useState(0);
    const [fencingAttachments, setFencingAttachments] = useState(false);
    const [fencingAttachmentsDescription, setFencingAttachmentsDescription] = useState("");
    const [createFinalInvoice, setCreateFinalInvoice] = useState(true);
    const [jobPickedUpNotes, setJobPickedUpNotes] = useState("");
    const [fencePartPickupFee, setFencePartPickupFee] = useState(0);
    const [fencePartPickupFeeFormatted, setFencePartPickupFeeFormatted] = useState("$0.00");
    const [showPickupConfirm, setShowPickupConfirm] = useState(false);
    const [pickupConfirmJobIds, setPickupConfirmJobIds] = useState<ConfirmJobIdList[]>([]);
    const [searchingPickups, setSearchingPickups] = useState(false);
    const [schedulingPickups, setSchedulingPickups] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetPickupData', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(result => {
            setTruckOptions(result.data.truckOptions);
            setYesNoOptions(result.data.yesNo);
            setPickupTravelPriceNote(result.data.pickupTravelPriceNote);
            setPickupTravelExtraHoursPriceNote(result.data.pickupTravelExtraHoursPriceNote);
            setLoading(false);
            
        })
        .catch(error => {
            toast.error(error.message);
        });       
    }

    //search for jobs
    const searchingJobs = () => {
        //searching again so need to clear existing results
        resetQuestions();
        setSearchingPickups(false);
        setResults([]);
        setErrors({});
        setErrorMessage("");
        setShowResults(false);
    }

    const addJob = (job: JobSearchResults) => {
        setSelectedJobs([...selectedJobs, job]);
        resetQuestions();
        setSearchingPickups(true);
        searchJobProducts(job);
    }

    const removeJob = (jobId: number) => {
        //remove job and products from results
        setSelectedJobs((prevItems) => prevItems.filter(item => item.jobId !== jobId));
        setResults((prevItems) => prevItems.filter(item => item.jobId != jobId));
    }
    //end search for jobs

    const resetQuestions = () => {
        //reset the answers to the questions if we search again 
        setJobPickedUp(-1);
        setCreateFinalInvoice(true);
        setJobPickedUpNotes("");
        setTiltSlideClearance(-1);
        setPlumbingElectricityDisconnected(-1);
        setDisconnectionDate("");
        setFencePartPickup(- 1);
        setFencePartPickupPanels(0);
        setFencePartPickupFee(0);
        setFencePartPickupFeeFormatted("$0.00");
        setFencingAttachments(false);
        setFencingAttachmentsDescription("");
        setWeekdayAccess(-1);
        setPhoneCallRequired(-1);
    }

    const searchJobProducts = async (job: JobSearchResults) => {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetPickupItemsForScheduling?JobId=' + job.jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError && res.data.items.length === 0) {
                var errors = res.data.messages as any[];
                var errMessage = "";
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                    errMessage += errors[i].content;
                };
                setErrorMessage(errMessage);
                setSearchingPickups(false);
            } else {
                var items: AddNewResults[] = res.data.items;
                for (var i = 0; i < items.length; i++) {
                    //update the job details from our job search
                    items[i].itemIndex = i;
                    items[i].siteAddress = job.siteAddress;
                    items[i].customerId = job.customerId;
                    items[i].customer = job.customerName;
                    items[i].customerCreditCard = job.customerCreditCard;
                    items[i].customerDirectDebit = job.customerDirectDebit;
                    items[i].customerSendConfirmationEmail = job.customerSendConfirmationEmail;
                };
                setShowResults(true);
                if (res.data.isError) {
                    //may have an error with part pickups
                    var errors = res.data.messages as any[];
                    var errMessage = "";
                    for (var i = 0; i < errors.length; i++) {
                        toast.error(errors[i].content);
                        errMessage += errors[i].content;
                    };
                    setErrorMessage(errMessage);
                } else {
                    setErrorMessage("");
                }
                setResults(prevItems => [...prevItems, ...items]);
                setSearchingPickups(false);
            }
        })
        .catch(error => {
            setSearchingPickups(false);
            toast.error(error.message);
        });
    }

    const selectItem = (e: any, updatedIndex: number) => {
        setResults(prevState => prevState.map((item, index) =>
            index == updatedIndex ? { ...item, selected: e.target.checked } : item
        ));

        var result = results[updatedIndex];

        //check if we can send email for this customer
        if (e.target.checked) {
            setSendEmailToCustomer(result.customerSendConfirmationEmail);
        } else {
            //check if any other jobs are checked
            var customersNoEmail = results.filter(r => !r.customerSendConfirmationEmail && r.selected && r.jobId != result.jobId);
            var canSendEmail = customersNoEmail.length === 0;
            setSendEmailToCustomer(canSendEmail);
        }

    }

    //DEV ONLY
    const selectJobs = (e: any) => {
        e.preventDefault();

        var updateResults = [...results];
        for (var i = 0; i < updateResults.length; i++) {
            updateResults[i].selected = true;
            var today = DateHelpers.getCurrentDate();
            updateResults[i].pickupDate = today;
        }
        setResults(updateResults);
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    //grid item changed
    const onCellChange = (e: any, updatedIndex: number) => {
        setErrors({});

        setResults(prevState => prevState.map((item, index) =>
            index == updatedIndex ? { ...item, [e.target.name]: e.target.value } : item
        ));
    }

    const onCellQuantityChange = (e: any, updatedIndex: number) => {
        setErrors({});

        setResults(prevState => prevState.map((item, index) =>
            index == updatedIndex ? { ...item, quantityScheduled: Number(e.target.value) } : item
        ));
    }

    const onCellTruckChange = (e: any, updatedIndex: number) => {
        setErrors({});
        setTiltSlideClearance(-1);

        setResults(prevState => prevState.map((item, index) =>
            index == updatedIndex ? { ...item, truck: Number(e.target.value) } : item
        ));
    }

    const handleJobPickedUpChange = (e: any) => {
        clearErrors(e.target.name);
        let value = Number(e.target.value);
        setJobPickedUp(value);

        var wkDayAccess = -1;
        var phCallReq = -1;
        if (value == 1) {
            //default weekday access and phone call
            wkDayAccess = 1;
            phCallReq = 0;
        }
        setWeekdayAccess(wkDayAccess);
        setPhoneCallRequired(phCallReq);
        setCreateFinalInvoice(true);
    }

    const handleFinalInvoiceCheckboxChange = (e: any) => {
        setCreateFinalInvoice(e.target.checked);
    }

    const handlejobPickedUpNotesChange = (e: any) => {
        clearErrors(e.target.name);
        setJobPickedUpNotes(e.target.value);
    }

    const handlePartPickupChange = (e: any) => {
        clearErrors(e.target.name);

        var value = e.target.value * 1;
        if (value === 1) {
            //part pickup so need to get the callout fee that the customer will be charged
            getPartPickupFee(value);
        } else {
            setFencePartPickup(value);
            setFencePartPickupPanels(0);
        }
    }

    const getPartPickupFee = async (partPickup: number) => {
        const token = await authService.getAccessToken();

        var items = results.filter(r => r.selected);
        if (items.length > 0) {
            var firstItem = items[0];       //the items should be for the same job / same customer so just get the first one

            axios.get('api/Schedule/GetFencePartPickupFee?CustomerId=' + firstItem.customerId, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(result => {
                setFencePartPickupFee(result.data.fencePartPickupFee);
                setFencePartPickupFeeFormatted(result.data.fencePartPickupFeeFormatted);
                setFencePartPickup(partPickup);
                setFencePartPickupPanels(0);
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    const handleFencingAttachmentsChange = (e: any) => {
        clearErrors(e.target.name);

        setFencingAttachments(e.target.checked);
        setFencingAttachmentsDescription("");
    }

    const handleSendEmailCheckboxChange = (e: any) => {
        setSendEmailToCustomer(e.target.checked);
    }

    const handleTruckClearanceChange = (e: any) => {
        clearErrors(e.target.name);

        var value = e.target.value * 1;

        setTiltSlideClearance(value);

        if (value === 0) {
            //if there isn't enough clearance then we need to change the trucks to crane for selected jobs

            //find crane truck
            var craneTruck = truckOptions.filter(t => t.name.toLowerCase() === 'crane');
            if (craneTruck.length > 0) {
                let craneTruckId = craneTruck[0].id;
                //update all selected items to use the Crane Truck
                setResults(prevState => prevState.map(item =>
                    item.selected ? { ...item, truck: craneTruckId } : item
                ));
            }
        }
    }

    //Notes Popup 
    const editJobNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        var item = results[itemIndex];
        var updatedEditNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: itemIndex,
            id: 0,
            itemId: item.itemId,
            item: item.item,
            notes: item.notes
        }
        setEditNotes(updatedEditNotes);
        setShowEditNotesModal(true);
    }

    const hideNotes = () => {
        var resetEditNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }
        setEditNotes(resetEditNotes);
        setShowEditNotesModal(false);
    }

    const updateNotes = (updatedEditNotes: EditNotes) => {
        setEditNotes(prevState => ({ ...prevState, notes: updatedEditNotes.notes }));
    }

    const saveNotes = () => {
        saveItemNotes();
    }

    const saveItemNotes = () => {
        //save notes
        var jobIndex = editNotes.rowIndex;
        var updatedNotes = editNotes.notes;

        setResults(prevState => prevState.map((item, index) =>
            index == jobIndex ? { ...item, notes: updatedNotes } : item
        ));

        //reset & hide popup
        hideNotes();
    }
    //End Notes Popup

    const validate = (jobs: AddNewResults[]) => {
        let errorList: any = {};
        let formIsValid = true;
        var rowNumber = 0;
        var customerId = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < jobs.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            //check to make sure they are scheduling jobs for the same customer as I need to send an email to the customer with the pickup details
            if (i === 0) {
                customerId = jobs[i].customerId;
            } else if (customerId != jobs[i].customerId) {
                formIsValid = false;
                errorList["multipleJobIds"] = "Pickups MUST be for the SAME customer!";
            }

            if (jobs[i].truck < 1 && jobs[i].isEws && jobs[i].itemTypeId != 21) {
                newErrorString += " Truck, ";
                error = true;
            }

            if (!jobs[i].pickupDate) {
                newErrorString += " Pick Up Date, ";
                error = true;
            } else if (!DateHelpers.isValidDate(jobs[i].pickupDate)) {
                newErrorString += " Valid Pick Up Date, ";
                error = true;
            }

            //if an offhire date is entered, validate it
            if (jobs[i].offhireDate && !DateHelpers.isValidDate(jobs[i].offhireDate)) {
                newErrorString += " Valid Offhire Date, ";
                error = true;
            } else if (jobs[i].offhireDate && jobs[i].offhireDate != null && !DateHelpers.compareDate(jobs[i].offhireDate ?? "", jobs[i].pickupDate)) {
                newErrorString += " Offhire Date MUST be <= Pick Up Date, ";
                error = true;
            }

            var qtyScheduled = jobs[i].quantityScheduled * 1;
            var qtyScheduledAvail = jobs[i].quantityScheduledAvailable * 1;
            if (qtyScheduled <= 0 || (qtyScheduled > qtyScheduledAvail)) {
                newErrorString += " Valid Quantity, ";
                error = true;
            }

            if (jobs[i].bringForwardDate && !DateHelpers.isValidDate(jobs[i].bringForwardDate)) {
                newErrorString += " Valid Bring Forward Date, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n";
                errorString += "Row " + (jobs[i].itemIndex + 1) + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (jobPickedUp < 0) {
            formIsValid = false;
            errorList["jobPickedUp"] = "Item Already Picked Up is required";
        }

        if (jobPickedUp === 1 && jobPickedUpNotes.trim() === "") {
            formIsValid = false;
            errorList["jobPickedUpNotes"] = "Notes are required";
        }

        if (jobPickedUp < 1) {
            //only answer questions if the job hasn't been picked up from site already
            //these are only used to help the drivers
            let isEwsItem = jobs.filter(q => q.isEws && q.itemTypeId != 21).length > 0;
            let isTiltSlide = jobs.filter(q => q.truck === 1).length > 0;
            if (isEwsItem) {
                if (isTiltSlide && tiltSlideClearance < 0) {
                    formIsValid = false;
                    errorList["tiltSlideClearance"] = "Tilt Slide Clearance is required!";
                }
                if (plumbingElectricityDisconnected < 0) {
                    formIsValid = false;
                    errorList["plumbingElectricityDisconnected"] = "Plumbing Electricity Disconnected is required!";
                }
            }
            let isPowerPole = jobs.filter(q => q.itemTypeId === 23).length > 0;
            if (isPowerPole) {
                if (plumbingElectricityDisconnected < 0) {
                    formIsValid = false;
                    errorList["plumbingElectricityDisconnected"] = "Electricity Disconnected is required!";
                } else if (plumbingElectricityDisconnected === 0) {
                    formIsValid = false;
                    errorList["plumbingElectricityDisconnected"] = "Customer MUST get electricity disconnected before scheduling pick up!";
                } else if (plumbingElectricityDisconnected === 1) {
                    //need disconnection date
                    if (!disconnectionDate) {
                        formIsValid = false;
                        errorList["disconnectionDate"] = "Disconnection Date is required!";
                    }
                }
            }

            let isFencing = jobs.filter(q => q.itemTypeId === 50).length > 0;
            if (isFencing) {
                if (fencePartPickup < 0) {
                    formIsValid = false;
                    errorList["fencePartPickup"] = "Is Fence Part Pickup is required!";
                }

                if (fencingAttachments && !fencingAttachmentsDescription) {
                    formIsValid = false;
                    errorList["fencingAttachmentsDescription"] = "Fencing Items are required!";
                }
            }

            if (weekdayAccess < 0) {
                formIsValid = false;
                errorList["weekdayAccess"] = "Weekday Access is required";
            }

            if (phoneCallRequired < 0) {
                formIsValid = false;
                errorList["phoneCallRequired"] = "Phone Call is required";
            }
        }

        if (error) {
            formIsValid = false;
            errorList["scheduleItems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const schedule = () => {
        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);
        setSchedulingPickups(true);
        setErrorMessage("");

        var jobs = [...results.filter(j => j.selected)];

        if (jobs.length === 0) {
            setSaveDisabled(false);
            setSchedulingPickups(false);
            toast.error("Please select job/s to schedule a pickup");
        } else {
            if (validate(jobs)) {
                if (jobPickedUp === 1) {
                    //we don't need to confirm the contact
                    scheduleJobs();
                } else {
                    //show confirm contacts screen for each job that is being scheduled (should just be one job)
                    //add jobs ids to list
                    var jobIds: ConfirmJobIdList[] = [];
                    for (var i = 0; i < jobs.length; i++) {
                        var job = jobs[i];
                        var existJobId = jobIds.filter(j => j.jobId == job.jobId);
                        if (existJobId.length === 0) {
                            //not added yet so add
                            var jobIdToAdd: ConfirmJobIdList = {
                                jobId: job.jobId,
                                hasInstall: true
                            };
                            jobIds.push(jobIdToAdd);
                        }
                    }
                    setShowPickupConfirm(true);
                    setPickupConfirmJobIds(jobIds);
                }
            } else {
                setSaveDisabled(false);
                setSchedulingPickups(false);
                toast.error("Please fix validation errors on screen!");
            }

        }
    }

    //confirm job contact/s before scheduling pickup
    const hideConfirmPickup = () => {
        setPickupConfirmJobIds([]);
        setShowPickupConfirm(false);
        setSchedulingPickups(false);
        setSaveDisabled(false);
    }

    const saveConfirmPickup = () => {
        setShowPickupConfirm(false);
        setErrorMessage("");
        scheduleJobs();
    }

    const scheduleJobs = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobs = [...results.filter(j => j.selected)];

        var schedule = {
            sendEmailToCustomer: sendEmailToCustomer,
            tiltSlideClearance: tiltSlideClearance,
            plumbingElectricityDisconnected: plumbingElectricityDisconnected,
            weekdayAccess: weekdayAccess,
            phoneCallRequired: phoneCallRequired,
            jobPickedUp: jobPickedUp,
            createFinalInvoice: createFinalInvoice,
            jobPickedUpNotes: jobPickedUpNotes,
            disconnectionDate: disconnectionDate,
            fencingPartPickup: fencePartPickup,
            fencingPartPickupPanels: fencePartPickupPanels,
            fencingAttachments: fencingAttachments,
            fencingAttachmentsDescription: fencingAttachmentsDescription,
            scheduleItems: jobs,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveSchedulePickup', schedule, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errMessage = "";
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                    errMessage += errors[i].content + " ";
                };
                setErrorMessage(errMessage);
            } else if (res.data.pickUpId === 0) {
                setPickupConfirmJobIds([]);
                setShowPickupConfirm(false);
                setSaveDisabled(false);
                setSchedulingPickups(false);
                setErrorMessage("There was an issue scheduling the pickup, please check if it has been scheduled!");
                toast.error("There was an issue scheduling the pickup, please check if it has been scheduled!");
            } else {
                var pickupId = res.data.pickUpId;
                if (!jobPickedUp) {
                    //need to either email the pickup request or just save it to the job
                    sendReport("JobPickupRequest", pickupId, user.sub)
                } else {
                    toast.success("Job Pickup Scheduled Successfully");
                    setPickupConfirmJobIds([]);
                    setSaveDisabled(false);
                    setShowResults(false);
                    setDisconnectionDate("");
                    setResults([]);
                    setTiltSlideClearance(-1);
                    setPlumbingElectricityDisconnected(-1);
                    setPhoneCallRequired(-1);
                    setWeekdayAccess(-1);
                    setJobPickedUp(-1);
                    setFencePartPickup(-1);
                    setFencePartPickupPanels(0);
                    setFencingAttachments(false);
                    setFencingAttachmentsDescription("");
                    setCreateFinalInvoice(true);
                    setJobPickedUpNotes("");
                    setFencePartPickupFee(0);
                    setFencePartPickupFeeFormatted("$0.00");
                    setSendEmailToCustomer(true);
                    setSchedulingPickups(false);
                    setJobTableKey((prevState) => prevState + 1);   //clear jobs table
                    setShowPickupConfirm(false);
                    setErrors({});
                }
            }
        })
        .catch(error => {
            setPickupConfirmJobIds([]);
            setShowPickupConfirm(false);
            setSaveDisabled(false);
            setSchedulingPickups(false);

            var errorMsg = "Pickup was NOT scheduled - ";
            if (error.response && error.response.data && error.response.data.length > 0) {
                errorMsg += error.response.data[0];
            } else {
                errorMsg += error.message;
            }
            setErrorMessage(errorMsg);
            toast.error(error.message);
        });
    }

    const sendReport = async (reportName: string, pickupId: number, subId: string) => {
        const token = await authService.getAccessToken();

        //get the data
        axios.get('api/Report/GetJobPickupRequestData?pickupId=' + pickupId + "&reportName=" + reportName, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                //Run Job Request / Quote Request report
                var report = new Stimulsoft.Report.StiReport();
                var reportFile = Stimulsoft.System.IO.File.getFile("reports/" + res.data.reportFileName);
                report.load(reportFile);
                report.dictionary.dataStore.clear();

                var reportData = JSON.stringify(res.data.reportData);
                //Dataset created
                var dataSet = new Stimulsoft.System.Data.DataSet("data");
                dataSet.readJson(reportData)
                report.regData(dataSet.dataSetName, "", dataSet);
                report.reportName = res.data.reportName;
                report.dictionary.connect(false);
                report.dictionary.synchronize();

                //add relationships between data
                for (var i = 0; i < res.data.reportRelationships.length; i++) {
                    var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(res.data.reportRelationships[i].sourceName, res.data.reportRelationships[i].name, res.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(res.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(res.data.reportRelationships[i].childTableName), [res.data.reportRelationships[i].parentFieldName], [res.data.reportRelationships[i].childFieldName]);
                    report.dictionary.relations.add(dataRelation);
                    report.dictionary.synchronize();
                }
                report.render();

                //Download the report to a pdf file
                var service = new Stimulsoft.Report.Export.StiPdfExportService();
                var stream = new Stimulsoft.System.IO.MemoryStream();
                var settings = new Stimulsoft.Report.Export.StiPdfExportSettings();
                service.exportTo(report, stream, settings);
                var bytes = stream.toArray();
    
                var fileData = [new Uint8Array(bytes)];
                var fileName = "Pickup Request";
                var saveFile = new File(fileData, fileName, { type: 'application/pdf' });

                //Send the report to the back-end to send the email to the customer
                const data = new FormData()
                data.append('tempPickupId', pickupId.toString());
                data.append('sendEmail', sendEmailToCustomer.toString());
                data.append('subId', subId);
                data.append('report', saveFile);
                
                //send report in an email
                axios.post('api/Job/SendPickupConfirmationEmail', data, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    if (res.data.isError) {
                        var errors = res.data.messages as any[];
                        errors.map(function (error: any) {
                            toast.error(error.content);
                        });
                    } else {
                        var emailMessage = " and Emailed Sent to Customer Successfully";
                        toast.success("Job Pickup Saved" + (sendEmailToCustomer ? emailMessage : ""));
                        setPickupConfirmJobIds([]);
                        setSaveDisabled(false);
                        setShowResults(false);
                        setDisconnectionDate("");
                        setResults([]);
                        setTiltSlideClearance(-1);
                        setPlumbingElectricityDisconnected(-1);
                        setPhoneCallRequired(-1);
                        setWeekdayAccess(-1);
                        setJobPickedUp(-1);
                        setFencePartPickup(-1);
                        setFencePartPickupPanels(0);
                        setFencingAttachments(false);
                        setFencingAttachmentsDescription("");
                        setCreateFinalInvoice(true);
                        setJobPickedUpNotes("");
                        setFencePartPickupFee(0);
                        setFencePartPickupFeeFormatted("$0.00");
                        setSendEmailToCustomer(true);
                        setSchedulingPickups(false);
                        setJobTableKey((prevState) => prevState + 1);   //clear jobs table
                        setShowPickupConfirm(false);
                        setErrors({});
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            }
        });
    }

    var minDate = DateHelpers.addDaysToCurrentDate(-14);
    var notes = <NotesPopup showEditNotesModal={showEditNotesModal} editNotes={editNotes} hideNotes={hideNotes} updateNotes={updateNotes} saveNotes={saveNotes} />
    let jobSearchControl = <JobSearchControlData key={jobTableKey} readonly={schedulingPickups} searching={searchingJobs} addJob={addJob} removeJob={removeJob} />
    let schedPickupConfirm = <SchedulePickupConfirmData showModal={showPickupConfirm} confirmJobIds={pickupConfirmJobIds} hideModal={hideConfirmPickup} saveModal={saveConfirmPickup} />
    let selectedProducts = results.filter(q => q.selected);

    let isEwsItem = selectedProducts.filter(q => q.isEws && q.itemTypeId != 21).length > 0;
    let isTiltSlide = selectedProducts.filter(q => q.truck === 1).length > 0;
    let isPowerPole = selectedProducts.filter(q => q.itemTypeId === 23).length > 0;
    let isFencing = selectedProducts.filter(q => q.itemTypeId === 50).length > 0;
    let isCreditCard = selectedProducts.filter(q => q.customerCreditCard).length > 0;
    let isDirectDebit = selectedProducts.filter(q => q.customerDirectDebit).length > 0;
    let canSendEmailToCustomer = selectedProducts.length > 0 ? selectedProducts[0].customerSendConfirmationEmail : true;    //customer has to be the same when picking up items so use first item

    const renderDetails = (
        <div>
            <div className="static-modal">
                {notes}
                {schedPickupConfirm}
            </div>
            {jobSearchControl}
            <span className={searchingPickups ? "marginLeft10 makeitcentervertical" : "hidden"}>Searching for Pickups...</span>
            <div className={schedulingPickups ? "" : "hidden"}>
                <span className="makeitred">...Scheduling Pickup/s - Please Wait...</span>
            </div>

            <div className="schedulePickupError">{errorMessage}</div>

            <div className={showResults && !schedulingPickups ? "" : "hidden"}>
                <div className={results.length > 0 ? '' : 'hidden'}>
                    <table className='table tableColours'>
                        <thead>
                            <tr>
                                <th className="hidden">Job Quote Item Id</th>
                                <th></th>
                                <th></th>
                                <th>Job Id</th>
                                <th>Site Address</th>
                                <th>Customer</th>
                                <th>P/O No</th>
                                <th>Item</th>
                                <th>Truck</th>
                                <th># On Site</th>
                                <th>Quantity</th>
                                <th>Hire Period</th>
                                <th>Pick Up Date</th>
                                <th>Offhire Date</th>
                                <th>Bring Forward</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((result, index) =>
                                <tr key={index}>
                                    <td className="hidden">{result.jobQuoteItemId}</td>
                                    <td className="table__text--align">{result.itemIndex + 1}</td>
                                    <td className="table__text--align"><input type='checkbox' className="input" checked={result.selected} onChange={(e) => selectItem(e, index)} /></td>
                                    <td className="table__text--align">
                                        <Link to={'/job/' + result.jobId} target="_blank" >
                                            {result.jobId}
                                        </Link>
                                    </td>
                                    <td className="table__text--align">
                                        <div className="tooltipitem  tooltipitem__right">{result.siteAddress ? result.siteAddress.substring(0, 30) + "... " : ""}
                                            <span className="tooltipitemtext  tooltipitemtext__right">{result.siteAddress}</span>
                                        </div>
                                    </td>
                                    <td className="table__text--align">{result.customer}</td>
                                    <td className="table__text--align">{result.purchaseOrderNumber}</td>
                                    <td className="table__text--align">
                                        <div className="tooltipitem tooltipitem__right">{result.itemShortcut}
                                            <span className="tooltipitemtext tooltipitemtext__right">{result.item}</span>
                                        </div>
                                    </td>
                                    <td className="table__text--align">
                                        <select className={result.canChangeTruck ? "select" : "hidden"} id="truck" name="truck" value={result.truck} onChange={(e) => onCellTruckChange(e, index)}>
                                            <option hidden defaultValue="-1" value={-1}></option>
                                            {truckOptions.map(truck =>
                                                <option key={truck.id} value={truck.id}>{truck.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        {result.quantityScheduledAvailable}
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min={1} step={1} id="quantityScheduled" name="quantityScheduled" className={result.quantityScheduledAvailable > 1 ? "form-control allocate__qtySize" : "hidden"} value={result.quantityScheduled} onChange={(e) => onCellQuantityChange(e, index)} />
                                        {result.quantityScheduledAvailable > 1 ? "" : result.quantityType}
                                    </td>
                                    <td className="table__text--align">{result.hirePeriod}</td>
                                    <td className="table__text--align">
                                        <input type="date" id="pickupDate" name="pickupDate" className="form-control" value={result.pickupDate} onChange={(e) => onCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='date' id="offhireDate" name="offhireDate" className="form-control" value={result.offhireDate ?? ""} onChange={(e) => onCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='date' min={minDate} id="bringForwardDate" name="bringForwardDate" className="form-control" value={result.bringForwardDate ?? ""} onChange={(e) => onCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align allocate-notes--width">
                                        <a className={result.notes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editJobNotes(e, index)}>
                                            <div className="tooltipitem tooltipitem__left">{result.notes ? result.notes.length < 60 ? result.notes : result.notes.substring(0, 60) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{result.notes}</span>
                                            </div>
                                        </a>
                                        <a className={result.notes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editJobNotes(e, index)}>
                                            <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                        </a>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {errors["scheduleItems"] ?
                    (errors["scheduleItems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
                <span className={errors["multipleJobIds"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["multipleJobIds"]}</span>

                <div className={isEwsItem && isTiltSlide ? "makeitred" : "hidden"}>Note: If Tilt Truck used for dropoff but Crane Truck required for pickup there will be an extra cost of {pickupTravelPriceNote}</div>
                <div className={isEwsItem && isTiltSlide ? "makeitred" : "hidden"}>Any travel over 3 hours will be charged at {pickupTravelExtraHoursPriceNote} per hour</div>

                <div className={results.length == 0 ? "hidden" : ""}>
                    <label className="input-group marginBottom10" htmlFor="jobPickedUp">
                        <span className="label-large">Item already picked up from site?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="Pickup will be created and completed without going through Logistics."></i></span>
                        <select className="select" id="jobPickedUp" name="jobPickedUp" value={jobPickedUp} onChange={(e) => handleJobPickedUpChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {yesNoOptions.map(yn =>
                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={jobPickedUp === 1 ? "marginLeftLabelLarge makeitred" : "hidden"}>Pickup Job WILL NOT go to Logistics</span>
                    <span className={errors["jobPickedUp"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["jobPickedUp"]}</span>

                    <label className={jobPickedUp === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="createFinalInvoice">
                        <span className="label-large">Create Final Invoice (if required)?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="If unticked, NO final invoice will be created. Note: PART Pickup will NOT create a Final Invoice"></i></span>
                        <input className="checkbox" type="checkbox" id="createFinalInvoice" name="createFinalInvoice" checked={createFinalInvoice} onChange={(e) => handleFinalInvoiceCheckboxChange(e)}></input>
                    </label>

                    <label className={jobPickedUp === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="jobPickedUpNotes">
                        <span className="label-large">Job Picked Up Notes</span>
                        <textarea className="input allocate__teamnotes" id="jobPickedUpNotes" name="jobPickedUpNotes" value={jobPickedUpNotes} onChange={(e) => handlejobPickedUpNotesChange(e)}></textarea>
                    </label>
                    <span className={errors["jobPickedUpNotes"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["jobPickedUpNotes"]}</span>
                </div>

                <div className={jobPickedUp < 1 ? "" : "hidden"}>
                    <label className={results.length == 0 ? "hidden" : "input-group marginBottom10"} htmlFor="sendEmailToCustomer">
                        <span className="label-large">Send Confirmation Email</span>
                        <input className="checkbox" type="checkbox" id="sendEmailToCustomer" name="sendEmailToCustomer" checked={sendEmailToCustomer} onChange={(e) => handleSendEmailCheckboxChange(e)} disabled={!canSendEmailToCustomer}></input>
                    </label>
                    <div className={isEwsItem ? "" : "hidden"}>
                        <label className={isTiltSlide ? "input-group marginBottom10" : "hidden"} htmlFor="tiltSlideClearance">
                            <span className="label-large">Is there 17m clearance for Tilt Slide Truck?</span>
                            <select className="select" id="tiltSlideClearance" name="tiltSlideClearance" value={tiltSlideClearance} onChange={(e) => handleTruckClearanceChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={errors["tiltSlideClearance"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["tiltSlideClearance"]}</span>
                    </div>
                    <div className={isEwsItem || isPowerPole ? "" : "hidden"}>
                        <label className="input-group marginBottom10" htmlFor="plumbingElectricityDisconnected">
                            <span className="label-large">{isEwsItem ? "Has the plumbing and/or electricity been disconnected?" : "Has the electricity been disconnected?"}</span>
                            <select className="select" id="plumbingElectricityDisconnected" name="plumbingElectricityDisconnected" value={plumbingElectricityDisconnected} onChange={(e) => { clearErrors(e.target.name); setPlumbingElectricityDisconnected(Number(e.target.value)) }}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={errors["plumbingElectricityDisconnected"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["plumbingElectricityDisconnected"]}</span>
                        <span className={isPowerPole && plumbingElectricityDisconnected === 0 ? "label errors" : "hidden"}>Customer needs to get the electricity disconnected before we can arrange pickup of the power pole!</span>
                        <span className={isPowerPole ? "label errors" : "hidden"}>Note 1 : Let customer know there will be a Callout Fee charged if the electricity hasn't been disconnected</span>
                        <span className={isPowerPole ? "label errors" : "hidden"}>Note 2: Ask the customer to email us a photo of the power pole</span>

                        <div className={isPowerPole && plumbingElectricityDisconnected === 1 ? "" : "hidden"}>
                            <label className="input-group marginBottom10" htmlFor="disconnectionDate">
                                <span className="label-large">Disconnection Date</span>
                                <input type='date' id="disconnectionDate" name="disconnectionDate" className='input' value={disconnectionDate} onChange={(e) => { clearErrors(e.target.name); setDisconnectionDate(e.target.value) }}></input>
                            </label>
                            <span className={errors["disconnectionDate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["disconnectionDate"]}</span>
                        </div>
                    </div>

                    <div className={isFencing ? "" : "hidden"}>
                        <label className="input-group marginBottom10" htmlFor="fencePartPickup">
                            <span className="label-large">Is this a part pickup?</span>
                            <select className="select" id="fencePartPickup" name="fencePartPickup" value={fencePartPickup} onChange={(e) => handlePartPickupChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <div className={fencePartPickup === 1 && !isCreditCard && !isDirectDebit ? "marginLeftLabelLarge makeitred" : "hidden"}>Customer will be charged {fencePartPickupFee > 0 ? fencePartPickupFeeFormatted : ""} for a part pickup</div>
                        <div className={fencePartPickup === 1 && isCreditCard ? "marginLeftLabelLarge makeitred" : "hidden"}>Charge Customers Credit Card {fencePartPickupFee > 0 ? fencePartPickupFeeFormatted : ""} for part pickup</div>
                        <div className={fencePartPickup === 1 && isDirectDebit ? "marginLeftLabelLarge makeitred" : "hidden"}>Customer needs to transfer {fencePartPickupFee > 0 ? fencePartPickupFeeFormatted : ""} via Direct Debit for part pickup</div>
                        <span className={errors["fencePartPickup"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fencePartPickup"]}</span>

                        <label className={fencePartPickup === 1 ? "input-group" : "hidden"} htmlFor="fencePartPickupPanels">
                            <span className="label-large">Estimated # Panels (use 0 if unknown)</span>
                            <input className="input" type="text" id="fencePartPickupPanels" name="fencePartPickupPanels" value={fencePartPickupPanels} onChange={(e) => { clearErrors(e.target.name);  setFencePartPickupPanels(Number(e.target.value)) }}></input>
                        </label>

                        <label className="input-group marginBottom10" htmlFor="fencingAttachments">
                            <span className="label-large">Are there any banners / signs / locks that need to be removed from the fence?</span>
                            <input className="checkbox" type="checkbox" name="fencingAttachments" checked={fencingAttachments} onChange={(e) => handleFencingAttachmentsChange(e)} ></input>
                        </label>
                        <span className={errors["fencingAttachments"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fencingAttachments"]}</span>

                        <label className={fencingAttachments ? "input-group" : "hidden"} htmlFor="fencingAttachmentsDescription">
                            <span className="label-large">Enter items that need to be removed </span>
                            <input className="input" type="text" id="fencingAttachmentsDescription" name="fencingAttachmentsDescription" value={fencingAttachmentsDescription} onChange={(e) => { clearErrors(e.target.name); setFencingAttachmentsDescription(e.target.value) }}></input>
                        </label>
                        <span className={errors["fencingAttachmentsDescription"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fencingAttachmentsDescription"]}</span>
                    </div>

                    <div className={results.length == 0 ? "hidden" : ""}>
                        <label className="input-group marginBottom10" htmlFor="weekdayAccess">
                            <span className="label-large">Is there clear access between 7am and 3pm weekdays?</span>
                            <select className="select" id="weekdayAccess" name="weekdayAccess" value={weekdayAccess} onChange={(e) => { clearErrors(e.target.name); setWeekdayAccess(Number(e.target.value)) }}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={errors["weekdayAccess"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["weekdayAccess"]}</span>

                        <label className="input-group marginBottom10" htmlFor="phoneCallRequired">
                            <span className="label-large">Phone call required prior to arrival?</span>
                            <select className="select" id="phoneCallRequired" name="phoneCallRequired" value={phoneCallRequired} onChange={(e) => { clearErrors(e.target.name); setPhoneCallRequired(Number(e.target.value)) }}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={errors["phoneCallRequired"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["phoneCallRequired"]}</span>
                    </div>
                </div>
                <div className={results.length == 0 ? "hidden" : "colAlignRight marginBottom10"}>
                    <button className="defaultbutton defaultbutton__large" type="button" onClick={schedule} disabled={saveDisabled}>Schedule</button>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return <div>
        <h1>Schedule Pickup</h1>
        {contents}
    </div>;

}

export default SchedulePickupData;
